import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  AccountBalanceWallet,
  AccessTime,
  CheckCircle,
  TrendingUp,
  Receipt,
  Download,
  Visibility,
  ArrowForward,
  Payment,
  LocalShipping,
  AccountBalance
} from '@mui/icons-material';
import api from '../services/api';
import { brand } from '../theme/colors';

const PAYMENT_METHODS = {
  'ecocash': 'EcoCash',
  'onemoney': 'OneMoney',
  'bank_transfer': 'Bank Transfer',
  'mukuru': 'Mukuru',
  'worldremit': 'WorldRemit'
};

const MIN_WITHDRAWAL = 100;

const MerchantPaymentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Available balance (role-aware GET /payments/balance)
  const [availableBalance, setAvailableBalance] = useState(null);

  // Withdrawal dialog
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    payment_method: 'ecocash',
    account_details: {
      phone: '',
      account_name: '',
      account_number: '',
      bank_name: ''
    }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Payment statistics
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingPayments: 0,
    ordersAwaitingPayment: 0,
    thisMonthEarnings: 0,
    totalOrders: 0,
    averageOrderValue: 0
  });
  
  // Payment data
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load orders to calculate payment stats. Balance comes from the
      // role-aware GET /payments/balance (same endpoint the contractor hub uses);
      // fall back to derived completed-order earnings if unavailable.
      const [ordersResponse, balanceResponse, withdrawalsResponse, profileResponse] = await Promise.all([
        api.get('/material-orders'),
        api.get('/payments/balance').catch(() => ({ data: { success: false } })),
        api.get('/payments/withdrawal-history').catch(() => ({ data: { success: false } })),
        api.get('/merchants/profile/me').catch(() => ({ data: { success: false } }))
      ]);

      if (withdrawalsResponse.data.success) {
        setWithdrawals(withdrawalsResponse.data.data || []);
      }
      if (profileResponse.data.success) {
        setMerchantProfile(profileResponse.data.data);
      }

      if (ordersResponse.data.success) {
        const allOrders = ordersResponse.data.data || [];
        setOrders(allOrders);

        // Calculate statistics
        // 'completed' means merchant has received payment (verified)
        // 'processing' means payment released from escrow but not yet verified as received
        const completedOrders = allOrders.filter(o => o.payment_status === 'completed');
        const processingOrders = allOrders.filter(o => o.payment_status === 'processing');
        const pendingOrders = allOrders.filter(o => 
          o.payment_status === 'held_in_escrow' || 
          o.status === 'delivered' || 
          o.status === 'confirmed'
        );
        const awaitingPaymentOrders = allOrders.filter(o => 
          o.status === 'confirmed' && o.payment_status === 'pending'
        );

        const totalEarnings = completedOrders.reduce((sum, order) => 
          sum + parseFloat(order.total_amount || 0), 0
        );

        const pendingPayments = pendingOrders.reduce((sum, order) => 
          sum + parseFloat(order.total_amount || 0), 0
        );

        // Calculate processing payments (released but not yet verified)
        const processingPaymentsAmount = processingOrders.reduce((sum, order) => 
          sum + parseFloat(order.total_amount || 0), 0
        );

        const thisMonth = new Date();
        thisMonth.setDate(1);
        const thisMonthEarnings = completedOrders
          .filter(o => new Date(o.completed_at || o.updated_at) >= thisMonth)
          .reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);

        const averageOrderValue = completedOrders.length > 0
          ? totalEarnings / completedOrders.length
          : 0;

        setStats({
          totalEarnings,
          pendingPayments,
          ordersAwaitingPayment: awaitingPaymentOrders.length,
          thisMonthEarnings,
          totalOrders: allOrders.length,
          completedOrdersCount: completedOrders.length,
          processingOrdersCount: processingOrders.length,
          averageOrderValue
        });

        // Available balance: prefer the canonical role-aware backend value;
        // fall back to verified+released earnings derived from orders.
        const backendAvailable = balanceResponse?.data?.success
          ? balanceResponse.data?.data?.available
          : null;
        const derivedAvailable = totalEarnings + processingPaymentsAmount;
        setAvailableBalance(
          backendAvailable !== null && backendAvailable !== undefined
            ? parseFloat(backendAvailable)
            : derivedAvailable
        );

        // Set payments from completed AND processing orders (processing payments should be visible)
        const allPayments = [...completedOrders, ...processingOrders];
        // Also include 'released' status for backward compatibility
        const releasedOrders = allOrders.filter(o => o.payment_status === 'released');
        allPayments.push(...releasedOrders);
        
        setPayments(allPayments.map(order => ({
          id: order.id,
          orderId: order.id,
          amount: parseFloat(order.total_amount || 0),
          status: order.payment_status || 'processing',
          date: order.completed_at || order.payment_released_at || order.updated_at,
          clientName: order.client_name,
          projectTitle: order.project_title,
          paymentMethod: 'Escrow Release',
          type: 'order_payment'
        })));
      }
    } catch (err) {
      console.error('Error loading payment data:', err);
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleWithdrawalSubmit = async () => {
    const amount = parseFloat(withdrawalForm.amount);
    if (!withdrawalForm.amount || isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'warning' });
      return;
    }
    if (amount < MIN_WITHDRAWAL) {
      setSnackbar({ open: true, message: `Minimum withdrawal amount is ${formatCurrency(MIN_WITHDRAWAL)}`, severity: 'warning' });
      return;
    }
    if (availableBalance !== null && amount > availableBalance) {
      setSnackbar({ open: true, message: 'Insufficient balance', severity: 'error' });
      return;
    }

    setSubmittingWithdrawal(true);
    try {
      await api.post(`/payments/merchant/${user?.id}/withdraw`, {
        amount,
        paymentMethod: withdrawalForm.payment_method,
        paymentDetails: withdrawalForm.account_details,
        currency: 'USD'
      });

      setSnackbar({
        open: true,
        message: 'Withdrawal request submitted! Processing typically takes 1-3 business days.',
        severity: 'success'
      });
      setWithdrawalDialog(false);
      setWithdrawalForm({
        amount: '',
        payment_method: 'ecocash',
        account_details: { phone: '', account_name: '', account_number: '', bank_name: '' }
      });
      loadPaymentData();
    } catch (err) {
      console.error('Error submitting withdrawal:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to submit withdrawal request',
        severity: 'error'
      });
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const handleExportCsv = () => {
    if (!payments.length) {
      setSnackbar({ open: true, message: 'No payments to export', severity: 'info' });
      return;
    }
    const headers = ['Order ID', 'Client', 'Project', 'Amount (USD)', 'Date', 'Status'];
    const escape = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
    const rows = payments.map((p) => [
      escape(p.orderId),
      escape(p.clientName || 'N/A'),
      escape(p.projectTitle || 'N/A'),
      escape((p.amount || 0).toFixed(2)),
      escape(formatDate(p.date)),
      escape(p.status || 'unknown')
    ].join(','));
    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `merchant-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, subtitle, icon, color, onClick, trend }) => {
    const IconComponent = icon;
    
    return (
      <Card
        sx={{
          height: '100%',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': onClick ? {
            boxShadow: 6,
            transform: 'translateY(-4px)'
          } : {},
          borderLeft: `4px solid ${color}`,
          position: 'relative',
          overflow: 'hidden'
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: color, mb: 0.5 }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" color="success.main">
                    {trend}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IconComponent sx={{ fontSize: 28, color: color }} />
            </Box>
          </Box>
          {onClick && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                View Details
              </Typography>
              <ArrowForward sx={{ fontSize: 16, color: 'primary.main' }} />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Payments & Earnings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track your earnings, pending payments, and payment history
        </Typography>
      </Box>

      {/* Available balance + withdraw */}
      <Card sx={{ mb: 4, borderLeft: `4px solid ${brand.primary}` }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} sm>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                Available Balance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: brand.primary }}>
                {availableBalance !== null ? formatCurrency(availableBalance) : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Released earnings ready to withdraw (min {formatCurrency(MIN_WITHDRAWAL)})
              </Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                size="large"
                startIcon={<AccountBalance />}
                onClick={() => setWithdrawalDialog(true)}
                disabled={!availableBalance || availableBalance < MIN_WITHDRAWAL}
                sx={{ bgcolor: brand.primary, fontWeight: 700, '&:hover': { bgcolor: brand.primaryDark } }}
              >
                Withdraw
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Earnings"
            value={formatCurrency(stats.totalEarnings)}
            subtitle={`From ${stats.completedOrdersCount || 0} verified payments`}
            icon={AccountBalanceWallet}
            color={brand.primary}
            onClick={() => {
              setActiveTab(1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            trend={stats.totalEarnings > 0 ? `${formatCurrency(stats.thisMonthEarnings)} this month` : null}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pending Payments"
            value={formatCurrency(stats.pendingPayments)}
            subtitle="Orders awaiting delivery/pickup confirmation"
            icon={AccessTime}
            color="#f59e0b"
            onClick={() => {
              // Filter to show pending orders
              setActiveTab(2);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Orders Awaiting Payment"
            value={stats.ordersAwaitingPayment}
            subtitle="Orders confirmed, payment processing"
            icon={Payment}
            color="#10b981"
            onClick={() => {
              setActiveTab(2);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="This Month Earnings"
            value={formatCurrency(stats.thisMonthEarnings)}
            subtitle="Earnings in current month"
            icon={TrendingUp}
            color="#8b5cf6"
            onClick={() => {
              setActiveTab(1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Orders"
            value={stats.totalOrders}
            subtitle={`All orders (${stats.completedOrdersCount || 0} completed, ${stats.processingOrdersCount || 0} processing)`}
            icon={Receipt}
            color="#ec4899"
            onClick={() => {
              navigate('/merchants/orders?filter=all');
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Payment History"
            value={payments.length}
            subtitle={`${payments.filter(p => p.status === 'completed').length} completed, ${payments.filter(p => p.status === 'processing' || p.status === 'released').length} processing`}
            icon={CheckCircle}
            color="#06b6d4"
            onClick={() => {
              setActiveTab(1);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Payment History" />
          <Tab label="Pending Payments" />
          <Tab label="Withdrawals" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Payment Overview
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Receipt />}
                    onClick={() => navigate('/merchant/orders')}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    View All Orders
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<LocalShipping />}
                    onClick={() => navigate('/merchant/orders')}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Deliveries
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportCsv}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Export Payment Report (CSV)
                  </Button>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Payment Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Bank Account:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: merchantProfile?.bank_details && Object.keys(merchantProfile.bank_details || {}).length > 0 ? 'success.main' : 'warning.main' }}>
                      {merchantProfile?.bank_details && Object.keys(merchantProfile.bank_details || {}).length > 0 ? 'Configured' : 'Not set up'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Mobile Money:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: merchantProfile?.mobile_money && Object.keys(merchantProfile.mobile_money || {}).length > 0 ? 'success.main' : 'warning.main' }}>
                      {merchantProfile?.mobile_money && Object.keys(merchantProfile.mobile_money || {}).length > 0 ? 'Configured' : 'Not set up'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate('/merchants/profile')}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    Update Payment Methods
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper elevation={2}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All payments from material orders (completed and processing)
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No payment history yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {payment.orderId?.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{payment.clientName || 'N/A'}</TableCell>
                      <TableCell>{payment.projectTitle || 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell>{formatDate(payment.date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            payment.status === 'completed' ? 'Completed (Payment Received)' :
                            payment.status === 'processing' || payment.status === 'released' ? 'Processing (Payment Released)' :
                            payment.status?.replace('_', ' ') || 'Unknown'
                          }
                          color={
                            payment.status === 'completed' ? 'success' :
                            payment.status === 'processing' || payment.status === 'released' ? 'info' :
                            'default'
                          }
                          size="small"
                          icon={payment.status === 'completed' ? <CheckCircle /> : <AccessTime />}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Order Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/orders/${payment.orderId}`)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper elevation={2}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pending Payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Orders awaiting delivery confirmation or payment release
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expected Release</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.filter(o => 
                  o.payment_status === 'held_in_escrow' || 
                  o.status === 'delivered' || 
                  o.status === 'confirmed'
                ).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No pending payments
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders
                    .filter(o => 
                      o.payment_status === 'held_in_escrow' || 
                      o.status === 'delivered' || 
                      o.status === 'confirmed'
                    )
                    .map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {order.id?.substring(0, 12)}...
                          </Typography>
                        </TableCell>
                        <TableCell>{order.client_name || 'N/A'}</TableCell>
                        <TableCell>{order.project_title || 'N/A'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {formatCurrency(parseFloat(order.total_amount || 0))}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status.replace('_', ' ')}
                            color="warning"
                            size="small"
                            icon={<AccessTime />}
                          />
                        </TableCell>
                        <TableCell>
                          {order.status === 'delivered' ? (
                            <Typography variant="body2" color="text.secondary">
                              Awaiting client confirmation
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              After delivery confirmation
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper elevation={2}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Withdrawal Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track the status of your withdrawal requests
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Requested</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No withdrawal requests yet. Use "Request Withdrawal" on the Overview tab.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  withdrawals.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{new Date(w.created_at).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(w.amount)}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{(w.payment_method || '').replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={w.status}
                          color={
                            w.status === 'completed' ? 'success'
                              : w.status === 'failed' || w.status === 'rejected' ? 'error'
                              : 'warning'
                          }
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{new Date(w.updated_at || w.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Withdrawal dialog */}
      <Dialog open={withdrawalDialog} onClose={() => setWithdrawalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Available balance: {availableBalance !== null ? formatCurrency(availableBalance) : '—'}
            </Alert>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={withdrawalForm.amount}
              onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: e.target.value }))}
              helperText={`Minimum withdrawal: ${formatCurrency(MIN_WITHDRAWAL)}`}
              sx={{ mb: 2 }}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />

            <TextField
              fullWidth
              select
              label="Payment Method"
              value={withdrawalForm.payment_method}
              onChange={(e) => setWithdrawalForm(prev => ({ ...prev, payment_method: e.target.value }))}
              sx={{ mb: 2 }}
            >
              {Object.entries(PAYMENT_METHODS).map(([key, name]) => (
                <MenuItem key={key} value={key}>{name}</MenuItem>
              ))}
            </TextField>

            {withdrawalForm.payment_method === 'ecocash' || withdrawalForm.payment_method === 'onemoney' ? (
              <TextField
                fullWidth
                label="Phone Number"
                value={withdrawalForm.account_details.phone}
                onChange={(e) => setWithdrawalForm(prev => ({
                  ...prev,
                  account_details: { ...prev.account_details, phone: e.target.value }
                }))}
                sx={{ mb: 2 }}
              />
            ) : withdrawalForm.payment_method === 'bank_transfer' ? (
              <>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={withdrawalForm.account_details.account_name}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Account Number"
                  value={withdrawalForm.account_details.account_number}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_number: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={withdrawalForm.account_details.bank_name}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, bank_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleWithdrawalSubmit}
            disabled={submittingWithdrawal}
            sx={{ bgcolor: brand.primary, fontWeight: 700, '&:hover': { bgcolor: brand.primaryDark } }}
          >
            {submittingWithdrawal ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MerchantPaymentsPage;

